import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Loader2, Download, CreditCard, Settings2, Save, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

/**
 * Modal to configure accepted payment methods for a given lease/transaction
 * and generate the tenant-facing PDF sheet.
 *
 * Props:
 *   - open, onClose
 *   - transactionId (required)
 *   - tenantName (optional, for display)
 */
const PaymentMethodsModal = ({ open, onClose, transactionId, tenantName }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [methods, setMethods] = useState([]);
  const [acceptedKeys, setAcceptedKeys] = useState([]);
  const [explicit, setExplicit] = useState(false);

  const fetchData = useCallback(async () => {
    if (!transactionId) return;
    setLoading(true);
    try {
      const r = await api.get(`/transactions/${transactionId}/accepted-payment-methods`);
      setMethods(r.data?.methods || []);
      setAcceptedKeys(r.data?.accepted_keys || []);
      setExplicit(!!r.data?.explicit);
    } catch (e) {
      toast.error('Impossible de charger les modes de paiement');
    } finally {
      setLoading(false);
    }
  }, [transactionId]);

  useEffect(() => {
    if (open) fetchData();
  }, [open, fetchData]);

  const toggleKey = (key) => {
    setAcceptedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/transactions/${transactionId}/accepted-payment-methods`, {
        accepted_keys: acceptedKeys,
      });
      setExplicit(true);
      toast.success('Modes de paiement enregistrés');
      // Fermer automatiquement pour libérer l'UI dessous (Radix overlay)
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/transactions/${transactionId}/payment-methods/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const safe = (tenantName || 'locataire').replace(/\s+/g, '_').toLowerCase();
      link.href = url;
      link.download = `modes_paiement_${safe}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche PDF téléchargée');
    } catch (e) {
      toast.error('Téléchargement impossible');
    } finally {
      setDownloading(false);
    }
  };

  const typeLabels = {
    mobile_money: { label: 'Mobile Money', chip: 'bg-orange-100 text-orange-700 border-orange-200' },
    bank:         { label: 'Banque',       chip: 'bg-sky-100 text-sky-700 border-sky-200' },
    cash:         { label: 'Espèces',      chip: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    check:        { label: 'Chèque',       chip: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    custom:       { label: 'Autre',        chip: 'bg-gray-100 text-gray-700 border-gray-200' },
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="pm-modal">
        <div className="p-5 bg-gradient-to-br from-[#2B3A2C] to-[#3E4F3F] text-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5" strokeWidth={1.6} /> Modes de paiement du loyer
            </DialogTitle>
            <DialogDescription className="text-white/85 font-body-modern">
              Cochez ceux que <b>{tenantName || 'ce locataire'}</b> peut utiliser. Générez ensuite la fiche PDF à lui remettre.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#B85C38]" />
            </div>
          ) : methods.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Aucun mode configuré pour votre agence.<br />
              Rendez-vous dans les paramètres pour ajouter des coordonnées.
            </div>
          ) : (
            <>
              {!explicit && (
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 font-body-modern flex items-start gap-2">
                  <Settings2 className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.6} />
                  <span>
                    Aucune restriction n'a encore été enregistrée pour ce bail — <b>tous les modes disponibles</b> sont acceptés par défaut.
                  </span>
                </div>
              )}

              <div className="space-y-2">
                {methods.map((m) => {
                  const tl = typeLabels[m.type] || typeLabels.custom;
                  const checked = acceptedKeys.includes(m.key);
                  return (
                    <label
                      key={m.key}
                      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${
                        checked ? 'border-[#B85C38] bg-[#B85C38]/5' : 'border-gray-200 hover:border-[#B85C38]/40 hover:bg-[#F5EFE1]/40'
                      }`}
                      data-testid={`pm-method-${m.key}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleKey(m.key)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-display font-bold text-[#2B3A2C] text-sm">{m.label}</span>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${tl.chip}`}>
                            {tl.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700 font-body-modern break-all">
                          {m.value || '—'}
                          {m.iban ? ` · IBAN ${m.iban}` : ''}
                          {m.bank_name && m.type === 'bank' ? ` · ${m.bank_name}` : ''}
                        </p>
                        {m.holder && (
                          <p className="text-[11px] text-gray-500 mt-0.5 truncate">Bénéficiaire : {m.holder}</p>
                        )}
                        {m.notes && (
                          <p className="text-[11px] text-gray-400 mt-0.5 italic truncate">{m.notes}</p>
                        )}
                      </div>
                      {checked && <CheckCircle2 className="w-4 h-4 text-[#B85C38] flex-shrink-0 mt-0.5" strokeWidth={1.6} />}
                    </label>
                  );
                })}
              </div>

              <p className="text-[11px] text-gray-500 font-body-modern text-center">
                {acceptedKeys.length} / {methods.length} mode(s) sélectionné(s) pour ce locataire
              </p>
            </>
          )}
        </div>

        <div className="px-5 pb-5 pt-2 flex flex-wrap gap-2 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="pm-cancel-btn">
            Annuler
          </Button>
          <div className="flex-1" />
          <Button
            onClick={save}
            disabled={saving || methods.length === 0}
            className="bg-[#2B3A2C] hover:bg-[#3E4F3F] text-white rounded-xl"
            data-testid="pm-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" strokeWidth={1.6} />}
            Enregistrer
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={downloading || methods.length === 0}
            className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl"
            data-testid="pm-download-btn"
          >
            {downloading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" strokeWidth={1.6} />}
            Télécharger la fiche PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentMethodsModal;
